import { createDate, formatCurrentTimezone, getTimezone } from '@lingoda/dates';
import { ContextError } from '@lingoda/errors';
import { BrowserTimeSyncError } from '../error';
import { formatClockDifference } from './clockDifference';
import { getBrowserTimeZone } from './getBrowserTimeZone';

const ERROR_PREFIX = 'The check browser sync time failed';

export type ApiTimeResponse = {
    apiTimeUTC?: string;
    error?: Error;
};

export const checkTime = async (fetchApiTime: () => Promise<ApiTimeResponse>) => {
    try {
        const apiRequestedAt = new Date();
        const { apiTimeUTC, error } = await fetchApiTime();
        const apiReceivedAt = new Date();

        const request = {
            apiRequestedAt: apiRequestedAt.toISOString(),
            apiReceivedAt: apiReceivedAt.toISOString(),
        };

        if (error) {
            throw new ContextError(`${ERROR_PREFIX}: fetchApiTime failed: ${error.message}`, {
                context: { fetchApiTime: request },
                cause: error,
            });
        }

        if (!apiTimeUTC) {
            throw new ContextError(
                `${ERROR_PREFIX}: the result of fetchApiTime should be defined`,
                { context: { fetchApiTime: request } },
            );
        }

        const data = getBrowserTimeOutOfSyncErrorData({
            apiTimeUTC,
            apiRequestedAt,
            apiReceivedAt,
            browserTime: apiReceivedAt,
        });

        if (!data.isTimeSync) {
            throw new BrowserTimeSyncError(
                `${ERROR_PREFIX}: time is out of sync with the API time.`,
                {
                    context: { data },
                    // apiTimeUTC can carry an offset, so parse it to get the UTC hour
                    serverHour: new Date(apiTimeUTC).toISOString().slice(0, 13),
                },
            );
        }
    } catch (error) {
        if (error instanceof ContextError || error instanceof BrowserTimeSyncError) {
            throw error;
        }

        throw new ContextError(
            `${ERROR_PREFIX}: ${error instanceof Error ? error.message : 'unknown error'}`,
            {
                context: { originalError: { message: String(error) } },
                cause: error,
            },
        );
    }
};

interface SyncErrorParams {
    apiTimeUTC: string;
    apiRequestedAt: Date;
    apiReceivedAt: Date;
    browserTime: Date;
}

export const getBrowserTimeOutOfSyncErrorData = ({
    apiTimeUTC,
    apiRequestedAt,
    apiReceivedAt,
    browserTime,
}: SyncErrorParams) => {
    const userDefaultTimezone = getTimezone();
    const userTimeZoneOffset = formatCurrentTimezone('OOOO');

    const serverTime = new Date(apiTimeUTC);
    const browserApiTimeDifferenceMs = browserTime.getTime() - serverTime.getTime();

    const isTimeSync = Math.abs(browserApiTimeDifferenceMs) < MAX_TIME_DIFFERENCE_MS;

    const apiTimeFetchDifferenceMs = Math.abs(apiReceivedAt.getTime() - apiRequestedAt.getTime());

    const isApiTimeFetchTooLong = apiTimeFetchDifferenceMs > MAX_TIME_DIFFERENCE_MS;

    const data = {
        isTimeSync,
        MAX_TIME_DIFFERENCE_MS,
        browserTimeUTC: browserTime.toISOString(),
        apiTimeUTC,
        apiRequestedAtUTC: apiRequestedAt.toISOString(),
        apiReceivedAtUTC: apiReceivedAt.toISOString(),
        isApiTimeFetchTooLong,
        apiTimeFetchDifferenceMs,
        browserApiTimeDifferenceMs,
        browserApiTimeDifference: formatClockDifference({ browserTime, serverTime }),
        browserTimeZoneFixed: getBrowserTimeZone(),
        getBrowserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userDate: createDate().toString(),
        userDefaultTimezone,
        userTimeZoneOffset,
    };

    return data;
};

const MAX_TIME_DIFFERENCE_MS = 5 * 60 * 1000;
export const TRYING_TO_GENERATE_ZOOM_MEETING_TOO_EARLY_ERROR_MESSAGE =
    'Trying to generate Zoom meeting too early';
